import React, { useEffect } from "react";
import { FiX } from "react-icons/fi";

export function pickValue(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
}

function formatDetailDate(dt) {
  if (dt == null || dt === "") return null;
  const datt = new Date(dt);
  if (Number.isNaN(datt.getTime())) return String(dt);
  return `${datt.toLocaleDateString()} ${datt.toLocaleTimeString()}`;
}

function formatDetailMoney(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

/** Shared field map for wishlist / shipping / floor lot drawers */
export const LOT_DETAIL_FIELDS = [
  {
    label: "Registration",
    keys: ["registration", "reg", "registration_number", "Registration"],
  },
  {
    label: "Vehicle",
    keys: ["product_name", "vehicle", "name"],
  },
  {
    label: "Brand",
    keys: ["brand_name", "brand"],
  },
  {
    label: "Model",
    keys: ["model_name", "model"],
  },
  {
    label: "Year",
    keys: ["year", "year_name"],
  },
  {
    label: "Category",
    keys: ["product_category", "category"],
  },
  {
    label: "Description",
    keys: ["product_description", "description"],
  },
  {
    label: "Price",
    keys: [
      "buy_now_price",
      "product_price",
      "price",
      "start_price",
      "current_bid",
      "your_bid",
      "winningBid",
      "amount",
    ],
    type: "money",
  },
  {
    label: "Your bid",
    keys: ["your_bid", "myBidOffer", "offer", "bid_amount"],
    type: "money",
  },
  {
    label: "Status",
    keys: ["status", "auction_status", "shipping_status"],
  },
  {
    label: "Fuel",
    keys: ["fuel_type", "fuel"],
  },
  {
    label: "Product ID",
    keys: ["product_id", "productId", "id", "p_id"],
  },
];

/**
 * Right-side detail drawer used across dealers dashboard list pages.
 */
function DetailDrawer({
  item,
  eyebrow = "Details",
  titleKeys = ["product_name", "vehicle", "name"],
  registrationKeys = [
    "registration",
    "reg",
    "registration_number",
    "Registration",
  ],
  fields = LOT_DETAIL_FIELDS,
  onClose,
  footer,
  ariaLabel = "Item details",
}) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  if (!item) return null;

  const title = pickValue(item, titleKeys) || "Details";
  const registration = pickValue(item, registrationKeys) || "—";

  const rows = fields
    .map((field) => {
      const raw = pickValue(item, field.keys);
      if (raw == null) return null;
      let value = String(raw);
      if (field.type === "date") value = formatDetailDate(raw) || value;
      if (field.type === "money") value = formatDetailMoney(raw) || value;
      return { label: field.label, value };
    })
    .filter(Boolean);

  // Avoid repeating the title/registration already shown in the header
  const filteredRows = rows.filter((row) => {
    if (row.label === "Vehicle" && row.value === String(title)) return false;
    if (
      row.label === "Registration" &&
      String(row.value).toUpperCase() === String(registration).toUpperCase()
    ) {
      return false;
    }
    return true;
  });

  return (
    <div
      className="dd-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        className="dd-drawer__backdrop"
        aria-label="Close details"
        onClick={onClose}
      />
      <aside className="dd-drawer__panel">
        <header className="dd-drawer__head">
          <div>
            <p className="dd-drawer__eyebrow">{eyebrow}</p>
            <h2>{title}</h2>
            <p className="dd-drawer__reg">{String(registration).toUpperCase()}</p>
          </div>
          <button
            type="button"
            className="dd-btn dd-btn--ghost"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </header>
        <div className="dd-drawer__body">
          {filteredRows.length ? (
            <dl className="dd-drawer__dl">
              {filteredRows.map((row) => (
                <div key={row.label} className="dd-drawer__row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="dd-empty">
              <strong>No details available</strong>
              <p>This row does not include extra fields to display.</p>
            </div>
          )}
          {footer ? <div className="dd-drawer__foot">{footer}</div> : null}
        </div>
      </aside>
    </div>
  );
}

export default DetailDrawer;
